import { useEffect, useId, useState, useMemo, useCallback, useRef } from "react";
import { useProgramma } from "../ProgrammaShell";
import { portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { Button } from "@/client/components/ui/button";
import { Checkbox } from "@/client/components/ui/checkbox";
import { Notice } from "@/client/components/ui/notice";
import { cn } from "@/client/lib/utils";
import { CheckIcon, CheckCircle2Icon, AlertCircleIcon, InfoIcon, TriangleAlert } from "lucide-react";
// SINGLE SOURCE OF TRUTH for eligibility (see app/src/shared/programme-rules.ts). The portal imports
// these via the @/ alias so the rules are identical on client and server.
import {
  isOfferingEligibleForGroup,
  eligibleYearLevelsForGroup,
  type OfferingRules,
  type GroupRules,
} from "@/shared/programme-rules";
// Reuse the offering details dialog + TechKwadraat info dialog + warn notice from the vast/keuze
// section so the TechnoHub page stays visually identical (same "Meer informatie", same eigen-bijdrage
// copy) without duplicating that UI.
import {
  OfferingDetailsDialog,
  TechKwadraatInfoDialog,
  TechKwadraatInfoNotice,
  TechKwadraatNotice,
  type Programme,
} from "./VastKeuzeSection";

// The eigen-bijdrage rule, shown both in the section intro AND as the per-group inline warn-alert
// once a single group passes 2 workshops. One sentence, one source of truth.
const EIGEN_BIJDRAGE_COPY =
  "Er kunnen 2 workshops geboekt worden via het KUVO programma. Meer afname is mogelijk vanuit het TechKwadraat budget.";

// ── Plain shapes mirrored from the /eligible + /selections payloads (only the fields we use). ───
type Offering = {
  id: number; programmeId: number; providerOrganizationId: number;
  enrolmentModeCode: string; requiredYearLevels: string[] | null; participatingYearLevels: string[] | null; maxYearLevels: string[] | null;
  vastStackable: boolean; techKwadraat: boolean; sortOrder: number;
};
type OfferingYL = { offeringId: number; yearLevelCode: string };
type Provider = { id: number; legalName: string; displayName: string | null };
type GroupYL = { groupId: number; displayName: string; yearLevelCode: string; studentCount: number };
type Selection = { id: number; offeringId: number };
type SelGroup = { selectionId: number; classGroupId: number };
type SelGroupYL = { selectionId: number; classGroupId: number; yearLevelCode: string };
type TimePreference = { id: number; selectionId: number; preferenceKindCode: string; rankOrder: number | null; notes: string | null };
type TimePrefDateRange = { preferenceId: number; startDate: string; endDate: string };
type Group = { id: number; displayName: string; yearLevels: { code: string; count: number }[] };

const ylNum = (code: string) => parseInt(code.replace("groep_", ""), 10) || 99;

// WORKSHOP-CENTRIC TechnoHub page. Instead of stepping group-by-group, the school sees ONE card per
// TechnoHub workshop. Each card lists the school's groups that are ELIGIBLE for that workshop as
// aanvinkbare chips; checking a chip aanmeldt that group for the workshop (autosave), unchecking
// afmeldt it. The per-group technohub selection is the same school_offering_selection scoped to
// groups the rest of the portal uses, so this reuses the existing endpoints with the
// preserve-other-groups-on-the-shared-row and resend-time-preferences safeguards.
export function TechnoHubSection({ schoolId }: { schoolId: number }) {
  const { setSaveState, setLastSavedAt, primaryAction, setSubNavItems } = useProgramma();
  const optOutId = useId();

  const cachedElig = readPortalCache<any>(portalQueries.eligible(schoolId).queryKey);
  const cachedSel = readPortalCache<any>(portalQueries.selections(schoolId).queryKey);
  const [offerings, setOfferings] = useState<Offering[]>(() => cachedElig?.offerings ?? []);
  const [offeringYLs, setOfferingYLs] = useState<OfferingYL[]>(() => cachedElig?.offeringYearLevels ?? []);
  const [programmes, setProgrammes] = useState<Programme[]>(() => cachedElig?.programmes ?? []);
  const [providers, setProviders] = useState<Provider[]>(() => cachedElig?.providers ?? []);
  const [groupYLs, setGroupYLs] = useState<GroupYL[]>(() => cachedElig?.classGroupYearLevels ?? []);
  const [blockedOfferingIds, setBlockedOfferingIds] = useState<number[]>(() => cachedElig?.blockedOfferingIds ?? []);
  const [selections, setSelections] = useState<Selection[]>(() => cachedSel?.selections ?? []);
  const [selGroups, setSelGroups] = useState<SelGroup[]>(() => cachedSel?.groups ?? []);
  const [selGroupYLs, setSelGroupYLs] = useState<SelGroupYL[]>(() => cachedSel?.groupYearLevels ?? []);
  const [timePrefs, setTimePrefs] = useState<TimePreference[]>(() => cachedSel?.timePreferences ?? []);
  const [timePrefRanges, setTimePrefRanges] = useState<TimePrefDateRange[]>(() => cachedSel?.timePreferenceDateRanges ?? []);
  // Per-school section opt-out: "onze school neemt dit schooljaar geen workshops van TechnoHub af."
  // Mirrors Deskundigheidsbevordering / Maatwerk: an optional section reaches "done" via either an
  // opt-out OR >=1 selection. Seeded from the prefetched sectionOptOuts; load() revalidates.
  const [optedOut, setOptedOut] = useState<boolean>(() => (cachedSel?.sectionOptOuts ?? []).includes("technohub"));
  const [togglingOptOut, setTogglingOptOut] = useState(false);
  const [loading, setLoading] = useState(() => !cachedElig || !cachedSel);
  const [error, setError] = useState(false);

  // CONCURRENCY GUARD, identical to VastKeuzeSection: one save in flight at a time (savingRef), and
  // a monotonic load token (loadSeqRef) so a superseded reload can never commit stale state.
  const savingRef = useRef(false);
  const loadSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const isCurrent = () => seq === loadSeqRef.current;
    try {
      setError(false);
      const [selRes, eligRes] = await Promise.all([
        fetch(`/api/school/${schoolId}/selections`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/eligible`, { credentials: "include" }),
      ]);
      if (selRes.ok) {
        const d = await selRes.json();
        writePortalCache(portalQueries.selections(schoolId).queryKey, d);
        if (isCurrent()) {
          setSelections(d.selections ?? []); setSelGroups(d.groups ?? []); setSelGroupYLs(d.groupYearLevels ?? []);
          setTimePrefs(d.timePreferences ?? []); setTimePrefRanges(d.timePreferenceDateRanges ?? []);
          setOptedOut((d.sectionOptOuts ?? []).includes("technohub"));
        }
      }
      if (eligRes.ok) {
        const d = await eligRes.json();
        writePortalCache(portalQueries.eligible(schoolId).queryKey, d);
        if (isCurrent()) {
          setOfferings(d.offerings ?? []); setOfferingYLs(d.offeringYearLevels ?? []);
          setProgrammes(d.programmes ?? []); setProviders(d.providers ?? []); setGroupYLs(d.classGroupYearLevels ?? []);
          setBlockedOfferingIds(d.blockedOfferingIds ?? []);
        }
      }
      if ((!selRes.ok || !eligRes.ok) && isCurrent()) setError(true);
    } catch { if (isCurrent()) setError(true); }
    if (isCurrent()) setLoading(false);
  }, [schoolId]);

  useEffect(() => { void load(); }, [load]);

  // This section has NO per-group sub-items: TechnoHub is a single workshop-centric page, so we
  // publish an EMPTY sub-nav. The shell then renders TechnoHub as a single (un-expanded) step and
  // the primary button falls through to "advance to the next section" instead of walking groups.
  useEffect(() => { setSubNavItems([]); return () => setSubNavItems([]); }, [setSubNavItems]);

  const saved = () => {
    setSaveState("saved");
    setLastSavedAt(new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }));
  };

  const [saving, setSaving] = useState(false);

  const runSave = useCallback(async (mutate: () => Promise<boolean>) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveState("saving");
    try {
      const ok = await mutate();
      if (ok) { await load(); saved(); } else setSaveState("error");
    } catch { setSaveState("error"); }
    finally { savingRef.current = false; setSaving(false); }
  }, [load]);

  const groups = useMemo<Group[]>(() => {
    const m = new Map<number, Group>();
    for (const r of groupYLs) {
      if (!m.has(r.groupId)) m.set(r.groupId, { id: r.groupId, displayName: r.displayName, yearLevels: [] });
      m.get(r.groupId)!.yearLevels.push({ code: r.yearLevelCode, count: r.studentCount });
    }
    for (const g of m.values()) g.yearLevels.sort((a, b) => ylNum(a.code) - ylNum(b.code));
    const rank = (g: Group) => Math.min(...g.yearLevels.map((y) => ylNum(y.code)));
    return [...m.values()].sort((a, b) => rank(a) - rank(b) || a.yearLevels.length - b.yearLevels.length || a.displayName.localeCompare(b.displayName, "nl", { numeric: true }));
  }, [groupYLs]);

  const programmeById = useMemo(() => new Map(programmes.map((p) => [p.id, p])), [programmes]);
  const providerById = useMemo(() => new Map(providers.map((p) => [p.id, p])), [providers]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const blockedSet = useMemo(() => new Set(blockedOfferingIds), [blockedOfferingIds]);

  const ylByOffering = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const yl of offeringYLs) { if (!m.has(yl.offeringId)) m.set(yl.offeringId, new Set()); m.get(yl.offeringId)!.add(yl.yearLevelCode); }
    return m;
  }, [offeringYLs]);
  const offeringYLArray = useCallback((offeringId: number) => [...(ylByOffering.get(offeringId) ?? [])], [ylByOffering]);
  const toRules = useCallback((o: Offering): OfferingRules => ({
    id: o.id,
    enrolmentModeCode: o.enrolmentModeCode,
    requiredYearLevels: o.requiredYearLevels,
    participatingYearLevels: o.participatingYearLevels,
    maxYearLevels: o.maxYearLevels,
    vastStackable: o.vastStackable,
  }), []);
  const participatingByOffering = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const o of offerings) {
      const codes = o.participatingYearLevels?.length ? o.participatingYearLevels : [...(ylByOffering.get(o.id) ?? [])];
      m.set(o.id, new Set(codes));
    }
    return m;
  }, [offerings, ylByOffering]);

  const selectionByOffering = useMemo(() => new Map(selections.map((s) => [s.offeringId, s.id])), [selections]);
  const groupsBySelection = useMemo(() => {
    const m = new Map<number, number[]>();
    for (const sg of selGroups) { if (!m.has(sg.selectionId)) m.set(sg.selectionId, []); m.get(sg.selectionId)!.push(sg.classGroupId); }
    return m;
  }, [selGroups]);
  const ylsBySelGroup = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const r of selGroupYLs) { const k = `${r.selectionId}:${r.classGroupId}`; if (!m.has(k)) m.set(k, new Set()); m.get(k)!.add(r.yearLevelCode); }
    return m;
  }, [selGroupYLs]);

  // The ONLY workshops on this page: technohub offerings, in the admin-controlled order.
  const workshops = useMemo(
    () => offerings.filter((o) => o.enrolmentModeCode === "technohub").sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
    [offerings],
  );

  // The group's eligible-and-participating leerjaren for a workshop (shared rule).
  const eligibleYLs = useCallback((offeringId: number, g: Group) => {
    const o = offerings.find((x) => x.id === offeringId);
    if (!o) return [];
    return eligibleYearLevelsForGroup(toRules(o), offeringYLArray(offeringId), { yearLevels: g.yearLevels } as GroupRules);
  }, [offerings, toRules, offeringYLArray]);

  // Which of the school's groups are eligible for a given workshop (shared rule + not blocked).
  const eligibleGroupsFor = useCallback((o: Offering) =>
    groups.filter((g) => isOfferingEligibleForGroup(toRules(o), offeringYLArray(o.id), g.yearLevels.map((y) => y.code), blockedSet.has(o.id))),
    [groups, toRules, offeringYLArray, blockedSet]);

  const isSelectedForGroup = useCallback((offeringId: number, groupId: number) => {
    const selId = selectionByOffering.get(offeringId);
    return !!selId && (groupsBySelection.get(selId) ?? []).includes(groupId);
  }, [selectionByOffering, groupsBySelection]);

  // The full desired set of groups (with leerjaren) currently on a workshop's selection. We resend
  // these on every PUT so a group toggle never wipes the OTHER groups on the same shared selection
  // row (the server replaces ALL children on a PUT that carries a child array).
  const currentGroups = useCallback((offeringId: number) => {
    const selId = selectionByOffering.get(offeringId);
    if (!selId) return [] as { classGroupId: number; yearLevels: string[] }[];
    return (groupsBySelection.get(selId) ?? []).map((gid) => {
      const g = groupById.get(gid);
      const stored = ylsBySelGroup.get(`${selId}:${gid}`);
      const yls = stored && stored.size ? [...stored] : (g ? eligibleYLs(offeringId, g) : []);
      return { classGroupId: gid, yearLevels: yls };
    });
  }, [selectionByOffering, groupsBySelection, ylsBySelGroup, groupById, eligibleYLs]);

  // Persist the desired group set for a workshop. POST creates the selection (first group), DELETE
  // removes it (last group gone), PUT replaces the children. On a PUT we RESEND the current time
  // preferences too, because the server wipes all children of a selection on any PUT that carries a
  // child array, so an existing voorkeursperiode would otherwise be lost on a group toggle.
  const saveSelectionGroups = useCallback(async (offeringId: number, desired: { classGroupId: number; yearLevels: string[] }[]) => {
    const selId = selectionByOffering.get(offeringId);
    const payload = desired.map((d) => {
      const g = groupById.get(d.classGroupId);
      return { classGroupId: d.classGroupId, includeFullGroup: !g || d.yearLevels.length >= g.yearLevels.length, yearLevels: d.yearLevels };
    });
    let res: Response;
    if (desired.length === 0) {
      if (!selId) return true;
      res = await fetch(`/api/school/${schoolId}/selections/${selId}`, { method: "DELETE", credentials: "include" });
    } else if (!selId) {
      res = await fetch(`/api/school/${schoolId}/selections`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ offeringId, selectionStatusCode: "interested", groups: payload }),
      });
    } else {
      const tps = timePrefs
        .filter((tp) => tp.selectionId === selId)
        .map((tp) => {
          const r = timePrefRanges.find((x) => x.preferenceId === tp.id);
          return {
            preferenceKindCode: tp.preferenceKindCode,
            ...(tp.rankOrder != null ? { rankOrder: tp.rankOrder } : {}),
            ...(tp.notes ? { notes: tp.notes } : {}),
            ...(r ? { dateRange: { startDate: r.startDate, endDate: r.endDate } } : {}),
          };
        });
      res = await fetch(`/api/school/${schoolId}/selections/${selId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ groups: payload, ...(tps.length ? { timePreferences: tps } : {}) }),
      });
    }
    return res.ok;
  }, [schoolId, selectionByOffering, groupById, timePrefs, timePrefRanges]);

  // Toggle one group's enrolment on a workshop (autosave). Adds the group (with its eligible
  // leerjaren) when unchecked, removes it when checked; preserves the workshop's OTHER groups.
  const toggleGroup = useCallback((offeringId: number, g: Group) => {
    const selected = isSelectedForGroup(offeringId, g.id);
    void runSave(async () => {
      const cur = currentGroups(offeringId);
      const next = selected ? cur.filter((d) => d.classGroupId !== g.id) : [...cur, { classGroupId: g.id, yearLevels: eligibleYLs(offeringId, g) }];
      return saveSelectionGroups(offeringId, next);
    });
  }, [isSelectedForGroup, currentGroups, eligibleYLs, saveSelectionGroups, runSave]);

  // Per-group count of selected TechnoHub workshops, school-wide. Drives the "3+ -> eigen bijdrage"
  // warn-alert. One booking per (technohub selection, assigned group); we tally by group.
  const workshopOfferingIds = useMemo(() => new Set(workshops.map((o) => o.id)), [workshops]);
  const workshopCountByGroup = useMemo(() => {
    const technohubSelIds = new Set(selections.filter((s) => workshopOfferingIds.has(s.offeringId)).map((s) => s.id));
    const m = new Map<number, number>();
    for (const sg of selGroups) {
      if (!technohubSelIds.has(sg.selectionId)) continue;
      m.set(sg.classGroupId, (m.get(sg.classGroupId) ?? 0) + 1);
    }
    return m;
  }, [selections, selGroups, workshopOfferingIds]);
  // Any single group over the 2 free workshops -> the school is into the TechKwadraat-budget.
  const anyGroupOverLimit = useMemo(() => [...workshopCountByGroup.values()].some((n) => n > 2), [workshopCountByGroup]);

  // The school's TechnoHub selection rows (any workshop). Opting out clears these, so the section
  // reads as a clean "geen" answer; unchecking re-opens it. (One selection row per workshop, scoped
  // to its assigned groups — DELETE removes the row and all its group/voorkeur children.)
  const technohubSelections = useMemo(
    () => selections.filter((s) => workshopOfferingIds.has(s.offeringId)),
    [selections, workshopOfferingIds],
  );

  // Section opt-out: "onze school neemt dit schooljaar geen workshops van TechnoHub af." Mirrors
  // Deskundigheidsbevordering / Maatwerk. Turning it ON first withdraws any existing TechnoHub
  // selections (an opt-out and a keuze are contradictory), then sets the per-school opt-out row.
  // Turning it OFF deletes the opt-out row and re-opens the section.
  const toggleOptOut = useCallback(async (next: boolean) => {
    if (togglingOptOut || savingRef.current) return;
    setTogglingOptOut(true);
    setSaveState("saving");
    try {
      if (next) {
        for (const sel of technohubSelections) {
          const r = await fetch(`/api/school/${schoolId}/selections/${sel.id}`, { method: "DELETE", credentials: "include" });
          if (!r.ok) { setSaveState("error"); setTogglingOptOut(false); return; }
        }
      }
      const res = await fetch(`/api/school/${schoolId}/section-optouts/technohub`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ optedOut: next }),
      });
      if (res.ok) { setOptedOut(next); await load(); saved(); }
      else { setSaveState("error"); }
    } catch { setSaveState("error"); }
    setTogglingOptOut(false);
  }, [togglingOptOut, technohubSelections, schoolId, load, setSaveState]);

  // Publish whether the section is started/resolved is owned by the server rollup (we publish an
  // empty sub-nav); nothing to push here beyond the single intro + cards.

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;
  if (error) return (
    <Notice tone="error" icon={<AlertCircleIcon />} title="Kan het programma niet laden"
      action={<Button size="sm" variant="outline" onClick={() => { setLoading(true); void load(); }}>Opnieuw proberen</Button>}>
      Controleer uw internetverbinding en probeer het opnieuw.
    </Notice>
  );
  if (groups.length === 0) return (
    <Notice tone="info" icon={<AlertCircleIcon />} title="Nog geen groepen">
      Voeg eerst groepen toe bij Gegevens; daarna kiest u hier per workshop welke groepen meedoen.
    </Notice>
  );

  return (
    <div className="space-y-5">
      <p className="pb-1 text-sm leading-6 text-muted-foreground">
        Kies per workshop welke groepen meedoen. Vink de groepen aan waarvoor u interesse heeft; KUVO
        geeft uw interesse door aan TechnoHub Woerden, die de workshops zelf inplant.
      </p>

      {/* The eigen-bijdrage rule, always visible in the intro, as the same clean info-neutral alert +
          "Meer informatie" link used on the 3D printen / Lasersnijder cards, but with the TechnoHub
          message (2 workshops via KUVO, meer afname uit het TechKwadraat-budget). */}
      <TechKwadraatInfoNotice>{EIGEN_BIJDRAGE_COPY}</TechKwadraatInfoNotice>

      {/* Per-school opt-out: a graceful "we doen dit jaar niet mee", so deze optionele stap netjes
          op afgerond komt te staan zonder dat er iets verplicht is. Aanvinken trekt eventuele
          eerder gekozen workshops in; uitvinken opent de stap weer. Same component/layout as
          Deskundigheidsbevordering / Extra maatwerk. */}
      <div className="rounded-lg border bg-muted/20 px-4 py-3">
        <label htmlFor={optOutId} className="flex cursor-pointer gap-3">
          <Checkbox
            id={optOutId}
            checked={optedOut}
            onCheckedChange={(val) => void toggleOptOut(val === true)}
            disabled={togglingOptOut || saving}
            className="mt-0.5 shrink-0"
          />
          <span className="text-sm leading-5 text-foreground">
            Onze school neemt dit schooljaar geen workshops van TechnoHub af.
          </span>
        </label>
      </div>

      {optedOut ? (
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-8 text-center">
          <CheckCircle2Icon className="mx-auto text-emerald-600" size={22} />
          <p className="mt-2 text-sm text-muted-foreground">
            U heeft aangegeven dit schooljaar geen workshops van TechnoHub af te nemen. Wilt u toch
            iets kiezen? Haal het vinkje hierboven weg, dan opent deze stap weer.
          </p>
        </div>
      ) : workshops.length === 0 ? (
        <Notice tone="info" icon={<AlertCircleIcon />} title="Geen workshops beschikbaar">
          Er zijn dit jaar nog geen TechnoHub-workshops om uit te kiezen.
        </Notice>
      ) : (
        <div className="divide-y divide-border">
          {workshops.map((o) => {
            const p = programmeById.get(o.programmeId);
            if (!p) return null;
            const prov = providerById.get(o.providerOrganizationId);
            const elig = eligibleGroupsFor(o);
            // A group on THIS workshop that is itself over the 2-workshop limit triggers the warn.
            const overLimitHere = elig.some((g) => isSelectedForGroup(o.id, g.id) && (workshopCountByGroup.get(g.id) ?? 0) > 2);
            // Always reserve the image gutter (a same-size spacer when there is no image) so every
            // workshop title lines up at the same left point, with or without a picture.
            const img = p.imagePath
              ? <img src={p.imagePath} alt="" className="size-16 shrink-0 rounded-lg object-contain" loading="lazy" />
              : <div className="size-16 shrink-0" aria-hidden />;
            return (
              <article key={o.id} className="overflow-hidden rounded-xl">
                <div className="flex items-start gap-3 p-3">
                  {img}
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    {/* Title row: name + badges left, "Meer informatie" top-right. */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <h3 className="text-[15px] font-semibold leading-tight text-foreground">{p.title}</h3>
                          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">Workshop</span>
                          {p.isNew && <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">NIEUW</span>}
                        </div>
                        {/* These are TechnoHub workshops (KUVO only forwards the interest), so the
                            provider line reads TechnoHub Woerden, not KUVO Woerden. */}
                        <p className="mt-0.5 text-xs text-muted-foreground">TechnoHub Woerden</p>
                      </div>
                      <OfferingDetailsDialog
                        programme={p}
                        providerName="TechnoHub Woerden"
                        voorLeerjaren={null}
                        trigger={
                          <button type="button" className="shrink-0 cursor-pointer text-xs font-medium text-primary underline-offset-2 hover:underline">
                            Meer informatie
                          </button>
                        }
                      />
                    </div>

                    {/* Short description teaser (same as the vast/keuze cards) so the workshop card is
                        not too empty; the full text lives in "Meer informatie". */}
                    {p.shortDescription && (
                      <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">{p.shortDescription}</p>
                    )}

                    {/* TechKwadraat clarity: static on a techKwadraat workshop, or once a group on
                        this workshop passes its 2 free workshops. */}
                    {(o.techKwadraat || overLimitHere) && <TechKwadraatNotice />}

                    {/* The school's groups that are ELIGIBLE for this workshop, as aanvinkbare chips.
                        Checking aanmeldt the group (autosave), unchecking afmeldt it. */}
                    {elig.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Geen van uw groepen komt in aanmerking voor deze workshop.</p>
                    ) : (
                      <div className="mt-0.5">
                        <p className="text-xs font-medium text-muted-foreground">Welke groepen doen mee?</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {elig.map((g) => {
                            const on = isSelectedForGroup(o.id, g.id);
                            return (
                              <button
                                key={g.id}
                                type="button"
                                disabled={saving}
                                aria-pressed={on}
                                onClick={() => toggleGroup(o.id, g)}
                                className={cn(
                                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
                                  on ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-foreground hover:border-primary/40",
                                )}
                              >
                                {on && <CheckIcon size={13} />}
                                {g.displayName}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* The eigen-bijdrage warn-alert, shown once ANY single group passes its 2 free workshops. */}
      {anyGroupOverLimit && (
        <div className="rounded-md border border-amber-500/50 px-4 py-3 text-amber-600">
          <div className="flex gap-3">
            <TriangleAlert aria-hidden className="mt-0.5 shrink-0 opacity-60" size={16} />
            <div className="space-y-1 text-sm">
              <p className="font-medium">Let op: meer dan 2 workshops voor een groep</p>
              <p>{EIGEN_BIJDRAGE_COPY}</p>
            </div>
          </div>
        </div>
      )}

      <div className="pt-2">
        <Button size="lg" onClick={primaryAction.onClick}>{primaryAction.label}</Button>
      </div>
    </div>
  );
}
